import React, { useMemo, useCallback, useEffect, useState, useContext, useRef } from 'react';
import { PatientDataContext } from '../../../context/PatientDataContext';
import { useParams, useNavigate } from 'react-router-dom';
import Layout from '../../../components/Layout';
import Breadcrumb from '../../../components/Breadcrumb';
import DDSideNavbar from './components/DDSideNavbar';
import { DocumentTextIcon, SearchIcon, ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/solid';
import { useAuth } from '../../../hooks/useAuth';
import useWorkflowConfig from '../../../hooks/useWorkflowConfig';
import {
  ADHD_CONDITION_LABEL,
  buildDDNavSections,
  getDDRouteForSectionIndex,
  hasActionableOtherConditions,
  isAdhdPositiveDecision,
} from '../../../utils/diagnosticDecisionRouting';
import { isClinicianAssignedToStage } from '../../../utils/clinicianPermissions';
import { isSupportingDocStale } from '../../../utils/supportingDocsVisibility';
import { useEmbeddedReportUrl } from '../../../hooks/useEmbeddedReportUrl';
import { DocumentEmbedDialog } from './components/DocumentEmbedDialog';
import { formatDateTime, isValidTimestamp } from '../../../utils/timestampUtils';
import { auth } from '../../../firebase';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../../../firebase';
import PatientScalesPanel from '../5_patientOverview/components/PatientScalesPanel';
import ConditionSelectionPanelTOC from '../6_clinicalInterview/components/ConditionSelectionPanelTOC';
import useEmergingFormulations from '../../../hooks/useEmergingFormulations';
import SSIDecisionSummaryTables from '../7_semiStructuredInterview/components/SSIDecisionSummaryTables';
import DualClinicianEmergingNotes from './components/DualClinicianEmergingNotes';

/**
 * DD Review step for multi-step Diagnostic Decision workflows (TOC, SLaM).
 *
 * Sits BEFORE the Decision for ADHD page. Read-only consolidation of assessment
 * information so the Lead (or MDT) can review everything before deciding.
 *
 * TOC (isTOCWorkflow):
 *  1. Supporting Documents, Rating Scales
 *  2. Condition(s) Referred For + Emerging Differentials
 *  3. Emerging Formulation + Confidential Notes (single-clinician / SSI DF paths)
 *  Page title: "MDT Review"
 *
 * SLaM / 3 Borough (hasCIReview):
 *  1. Supporting Documents, Rating Scales
 *  2. SSI symptom / domain decisions (read-only)
 *  3. SSI + Lead Emerging Formulation and Confidential Notes side by side
 *  Page title: "Review" (SLaM does not run an MDT)
 *
 * Patients without multi-step DD are redirected to /adhd.
 * No Firestore writes — purely read-only.
 */
export default function DDReview() {
  const { patientId } = useParams();
  const navigate = useNavigate();
  const { patientDataModel, updatePatientFirestore } = useContext(PatientDataContext);
  const { claims } = useAuth();
  const wf = useWorkflowConfig(patientDataModel, claims);

  // ---------------------------------------------------------------------------
  // Route guard: redirect patients without multi-step DD away from this page
  // ---------------------------------------------------------------------------
  useEffect(() => {
    if (patientDataModel && !wf.hasMultiStepDD) {
      navigate(
        `/patient/${patientId}/proforma/diagnostic-decision/adhd`,
        { replace: true }
      );
    }
  }, [patientDataModel, wf.hasMultiStepDD, navigate, patientId]);

  // ---------------------------------------------------------------------------
  // "Entered" marker for the DD module (multi-step orgs: TOC, SLaM, 3 Borough)
  // ---------------------------------------------------------------------------
  // Review is the canonical entry point for multi-step DD. Visiting it means the
  // clinician has opened the module, so the Patient Overview card should show
  // partial progress (25%) and a "Continue" button that resumes at the ADHD
  // decision. We record entry by promoting the stage status
  // notStarted -> inProgress (same pattern as the Adult Autism DD module). This
  // is the only Firestore write on this otherwise read-only page; it is a
  // one-shot, gated no-op for read-only viewers, non-multi-step orgs, and any
  // stage that is already locked/inProgress/completed.
  const hasPromotedDDStatusRef = useRef(false);
  useEffect(() => {
    if (hasPromotedDDStatusRef.current) return;
    if (!patientDataModel || !wf.hasMultiStepDD) return;
    const uid = auth.currentUser?.uid;
    if (!uid || !isClinicianAssignedToStage(patientDataModel, uid, 'DD')) return;
    const ddStatus = patientDataModel?.dataModel?.['4_diagnosticDecision']?.status;
    if (ddStatus !== 'notStarted') return;
    hasPromotedDDStatusRef.current = true;
    updatePatientFirestore(patientId, {
      '4_diagnosticDecision': { status: 'inProgress' },
    });
  }, [patientDataModel, wf.hasMultiStepDD, patientId, updatePatientFirestore]);

  // ---------------------------------------------------------------------------
  // Informant data — fetched so PatientScalesPanel can display informant
  // rating scales without getting stuck on a permanent "Loading..." spinner.
  // Mirrors the fetch logic in PatientOverview.js exactly, including the
  // isCurrent guard to prevent race conditions on fast patient switches.
  // ---------------------------------------------------------------------------
  const [informantData, setInformantData] = useState({});

  useEffect(() => {
    let isCurrent = true;
    setInformantData({});

    const fetchInformantData = async () => {
      if (!patientDataModel?.informants || Object.keys(patientDataModel.informants).length === 0) {
        return;
      }

      const nextInformantsData = {};
      const informantIds = Object.keys(patientDataModel.informants);

      for (const informantId of informantIds) {
        try {
          const informantRef = doc(db, 'patients', informantId);
          const informantDoc = await getDoc(informantRef);

          if (!isCurrent) return;

          if (informantDoc.exists()) {
            nextInformantsData[informantId] = informantDoc.data();
          }
        } catch (error) {
          console.error(`Error fetching informant data for ${informantId}:`, error);
        }
      }

      if (isCurrent) {
        setInformantData(nextInformantsData);
      }
    };

    fetchInformantData();

    return () => { isCurrent = false; };
  }, [patientId, patientDataModel?.informants]);

  // ---------------------------------------------------------------------------
  // Supporting Documents — mirror of the document presence checks in 1_DD.js
  // ---------------------------------------------------------------------------
  // We also hide any document whose report section has regressed to the purple
  // "AI outputs ready for review" state (was approved, then edits regenerated
  // paragraphs) because the rendered PDF may be stale. And, matching
  // DocumentPreviewPanel, hide SSI while an SSI reset is pending.
  const manageReports = useMemo(() => patientDataModel?.dataModel?.['6_manageReports'], [patientDataModel]);
  const isSSIResetRequired = useMemo(() => manageReports?.ssiResetRequired === true, [manageReports]);
  const docs = useMemo(() => patientDataModel?.documents || {}, [patientDataModel]);
  const showCI      = useMemo(() => Boolean(docs.clinicalinterviewReport) && !isSupportingDocStale(manageReports, 'clinicalinterviewReport'), [docs, manageReports]);
  const showSSI     = useMemo(() => Boolean(docs.semistructuredinterviewReport) && !isSSIResetRequired && !isSupportingDocStale(manageReports, 'semistructuredinterviewReport'), [docs, isSSIResetRequired, manageReports]);
  const showQbCheck = useMemo(() => Boolean(docs.qbcheckReport) && !isSupportingDocStale(manageReports, 'qbcheckReport'), [docs, manageReports]);
  const showQbTest  = useMemo(() => Boolean(docs.qbtestReport) && !isSupportingDocStale(manageReports, 'qbtestReport'), [docs, manageReports]);
  const hasAnyDoc   = showCI || showSSI || showQbCheck || showQbTest;

  // Timestamps for each report, used to display "last updated" labels.
  const ciLastGenerated      = useMemo(() => docs.clinicalinterviewReport?.lastGenerated || null, [docs]);
  const ssiLastGenerated     = useMemo(() => docs.semistructuredinterviewReport?.lastGenerated || null, [docs]);
  const qbCheckLastGenerated = useMemo(() => docs.qbcheckReport?.lastGenerated || null, [docs]);
  const qbTestLastGenerated  = useMemo(() => docs.qbtestReport?.lastGenerated || null, [docs]);

  // Prefetch signed URLs so clicking View is near-instant.
  const { prefetch: prefetchCI }      = useEmbeddedReportUrl(patientId, 'Clinical Interview', ciLastGenerated, auth.currentUser?.uid);
  const { prefetch: prefetchSSI }     = useEmbeddedReportUrl(patientId, 'Semi-Structured Interview', ssiLastGenerated, auth.currentUser?.uid);
  const { prefetch: prefetchQbCheck } = useEmbeddedReportUrl(patientId, 'QbCheck', qbCheckLastGenerated, auth.currentUser?.uid);
  const { prefetch: prefetchQbTest }  = useEmbeddedReportUrl(patientId, 'QbTest', qbTestLastGenerated, auth.currentUser?.uid);

  // Dialog state for the report preview iframe
  const [selectedReport, setSelectedReport]       = useState(null);
  const [isDocumentDialogOpen, setIsDocumentDialogOpen] = useState(false);

  const handleOpenDocumentDialog  = (reportStage) => { setSelectedReport(reportStage); setIsDocumentDialogOpen(true); };
  const handleCloseDocumentDialog = () => { setIsDocumentDialogOpen(false); setTimeout(() => setSelectedReport(null), 300); };

  // ---------------------------------------------------------------------------
  // Emerging data — TOC uses single-source CI12/SSI3 (via hook fallback);
  // SLaM uses emergingFormulations for both SSI + Lead clinicians.
  // ---------------------------------------------------------------------------
  const emerging = useEmergingFormulations(patientDataModel);

  // TOC Emerging Differentials (CI section 12). ADHD shown separately above.
  // For dual-clinician SLaM, prefer the SSI clinician's differentials list
  // (what was recorded at SSI Review); fall back to Lead if SSI has none.
  const differentials = useMemo(() => {
    const source = emerging.isDualClinician
      ? (emerging.ssiClinician?.differentials?.length
          ? emerging.ssiClinician.differentials
          : (emerging.leadClinician?.differentials || []))
      : (emerging.leadClinician?.differentials || []);
    return source.filter((c) => c !== ADHD_CONDITION_LABEL);
  }, [emerging]);

  const ssiColleagueNotes = emerging.ssiClinician;
  const leadColleagueNotes = emerging.leadClinician;
  const tocNotes = emerging.leadClinician;

  const ssiDisplayName =
    ssiColleagueNotes?.updatedByName
    || patientDataModel?.clinicians?.clinicianSSI?.clinicianName
    || 'SSI clinician';
  const leadDisplayName =
    leadColleagueNotes?.updatedByName
    || patientDataModel?.clinicians?.clinicianLead?.clinicianName
    || 'Lead clinician';

  // "Last updated" per column. Prefer per-clinician updatedAt; otherwise use
  // module completion proxies so the Lead still sees when notes were last set.
  const ssiLastUpdated = useMemo(() => {
    if (ssiColleagueNotes?.updatedAt) return ssiColleagueNotes.updatedAt;
    const reportTs = patientDataModel?.documents?.semistructuredinterviewReport?.lastGenerated;
    if (reportTs) return reportTs;
    return patientDataModel?.dataModel?.['3_semiStructuredInterview']?.firstCompletedAt || null;
  }, [patientDataModel, ssiColleagueNotes?.updatedAt]);

  const leadLastUpdated = useMemo(() => {
    if (leadColleagueNotes?.updatedAt) return leadColleagueNotes.updatedAt;
    return patientDataModel?.dataModel?.['2_clinicalInterview']?.firstCompletedAt || null;
  }, [patientDataModel, leadColleagueNotes?.updatedAt]);

  // TOC single-column notes (legacy / single-clinician path)
  const formulationNotes = tocNotes?.formulation || '';
  const confidentialNotes = tocNotes?.confidentialNotes || '';

  // ---------------------------------------------------------------------------
  // SSI symptom / domain decisions (SLaM Review) — same derivation as 3_SSIReview
  // ---------------------------------------------------------------------------
  const ssiDecisionTables = useMemo(() => {
    if (!wf.hasCIReview) return null;

    const ssiData = patientDataModel?.dataModel?.['3_semiStructuredInterview']?.sections;
    if (!ssiData) {
      return {
        attentionDeficitData: [],
        hyperactivityImpulsivityData: [],
        attentionTotals: { adulthoodTotal: 0, childhoodTotal: 0 },
        hyperactivityTotals: { adulthoodTotal: 0, childhoodTotal: 0 },
        domainImpairmentData: [],
      };
    }

    const sections = Object.entries(ssiData).map(([key, section]) => ({ key, ...section }));
    const symptomsSection = sections.find((s) => s.sectionTitle === 'Symptoms');
    const domainsSection = sections.find((s) => s.sectionTitle === 'Domains of Functional Impairment');
    const questionsSymptoms = Object.entries(symptomsSection?.questions || {}).map(([, q]) => q);

    const attentionDeficitData = questionsSymptoms
      .filter((item) => item?.DSM_code && item.DSM_code.startsWith('A1'))
      .map((item) => ({
        criterion: item.DSM_code,
        symptom: item.DSM_symptom,
        adulthood: item.symptomPresent_Adulthood,
        childhood: item.symptomPresent_Childhood,
      }));
    const hyperactivityImpulsivityData = questionsSymptoms
      .filter((item) => item?.DSM_code && item.DSM_code.startsWith('A2'))
      .map((item) => ({
        criterion: item.DSM_code,
        symptom: item.DSM_symptom,
        adulthood: item.symptomPresent_Adulthood,
        childhood: item.symptomPresent_Childhood,
      }));

    const tot = (data) => ({
      adulthoodTotal: data.filter((item) => item.adulthood).length,
      childhoodTotal: data.filter((item) => item.childhood).length,
    });

    const domainImpairmentData = Object.entries(domainsSection?.questions || {})
      .sort(([a], [b]) => parseInt(a, 10) - parseInt(b, 10))
      .map(([, question]) => ({
        domain: question.DSM_symptom || question.DSM_symptom_prefix || 'Unknown',
        adulthood: question.functioningReduced_Adulthood,
        childhood: question.functioningReduced_Childhood,
      }));

    return {
      attentionDeficitData,
      hyperactivityImpulsivityData,
      attentionTotals: tot(attentionDeficitData),
      hyperactivityTotals: tot(hyperactivityImpulsivityData),
      domainImpairmentData,
    };
  }, [patientDataModel, wf.hasCIReview]);

  // MDT Review orgs (TOC, WLC) show "MDT Review"; others show "Review".
  const reviewPageTitle = (wf.isMDTReview || wf.isTOCWorkflow) ? 'MDT Review' : 'Review';

  // ---------------------------------------------------------------------------
  // DDSideNavbar — always currentSectionIndex=0 (Review is this page)
  // ---------------------------------------------------------------------------
  const ddNavSections = useMemo(
    () => buildDDNavSections({
      patientDataModel,
      isFlatDifferentials: wf.isFlatDifferentials,
      isTOCWorkflow: wf.isTOCWorkflow,
      isSLaMWorkflow: wf.isSLaMWorkflow,
      isMDTReview: wf.isMDTReview,
      isSembleMdtReportEnabled: wf.isSembleMdtReportEnabled,
      skipDifferentialsDecisions: wf.skipDifferentialsDecisions,
      currentSectionIndex: 0,
    }),
    [patientDataModel, wf.isFlatDifferentials, wf.isTOCWorkflow, wf.isSLaMWorkflow, wf.isMDTReview, wf.isSembleMdtReportEnabled, wf.skipDifferentialsDecisions]
  );

  // Must match buildDDNavSections' conditional Other Conditions step so
  // sidebar index 2 maps to Final Formulation when that step is omitted.
  const hasOtherConditions = useMemo(
    () => hasActionableOtherConditions({
      patientDataModel,
      isFlatDifferentials: wf.isFlatDifferentials,
      skipDifferentialsDecisions: wf.skipDifferentialsDecisions,
    }),
    [patientDataModel, wf.isFlatDifferentials, wf.skipDifferentialsDecisions],
  );

  // Whether MDT Plan appears in the sidebar for this patient.
  const ddSection1 = patientDataModel?.dataModel?.['4_diagnosticDecision']?.sections?.['1'];
  const hasMdtPlan = wf.isSembleMdtReportEnabled && isAdhdPositiveDecision(ddSection1);

  // Navigate between DD pages from the sidebar.  MDT Review has no pending
  // writes to flush, so we navigate directly without a flush step.
  const navigateToSection = useCallback((index) => {
    navigate(getDDRouteForSectionIndex(patientId, index, hasOtherConditions, hasMdtPlan));
  }, [patientId, navigate, hasOtherConditions, hasMdtPlan]);

  const breadcrumbTuples = [
    ['Patient Overview', `/patient/${patientId}`],
    ['Diagnostic Decision', null],
    [reviewPageTitle, null],
  ];

  if (!patientDataModel) return null;

  return (
    <Layout activePage="dashboard">
      <Breadcrumb tupleList={breadcrumbTuples} />

      {/* Outer flex row: DDSideNavbar (left) + page body (right) */}
      <div className="flex items-start">

        <DDSideNavbar
          sections={ddNavSections}
          currentSectionIndex={0}
          navigateToSection={navigateToSection}
        />

        <main className="flex-grow p-8">

          {/* Top bar: back button (left) + page title (centre) + next button (right) */}
          <div className="flex justify-between items-center mb-6">

            <div className="w-1/3 flex justify-start">
              <button
                onClick={() => navigate(`/patient/${patientId}`)}
                className="flex items-center py-2 px-4 rounded-md shadow-md text-gray-900 border border-gray-300 transition-all duration-300 hover:bg-gray-50"
              >
                <ChevronLeftIcon className="w-5 h-5 mr-2" aria-hidden="true" />
                <span className="font-bold">Back to Patient Overview</span>
              </button>
            </div>

            <div className="w-1/3 flex justify-center">
              <h1 className="text-2xl font-bold">{reviewPageTitle}</h1>
            </div>

            <div className="w-1/3 flex justify-end">
              <button
                onClick={() => navigate(`/patient/${patientId}/proforma/diagnostic-decision/adhd`)}
                className="flex items-center py-2 px-4 rounded-md shadow-md bg-violet-900 hover:bg-violet-800 text-white font-bold transition-all duration-300"
              >
                <span>Next: Decision for ADHD</span>
                <ChevronRightIcon className="w-5 h-5 ml-2" aria-hidden="true" />
              </button>
            </div>

          </div>

          {/* ----------------------------------------------------------------
              Panel 1: Supporting Documents
              Only rendered if at least one report has been generated.
          ---------------------------------------------------------------- */}
          {hasAnyDoc && (
            <div className="bg-gray-50 rounded-2xl border border-gray-200 p-6 mb-6">
              <h3 className="text-lg font-semibold mb-4 text-left">Supporting Documents</h3>
              <div className="flex gap-8 flex-wrap">

                {showCI && (
                  <div className="flex flex-col items-center text-center">
                    <DocumentTextIcon className="h-16 w-16 text-gray-400 mx-auto mb-2" />
                    <h4 className="font-medium text-gray-800 mb-1">Clinical Interview</h4>
                    {isValidTimestamp(ciLastGenerated) && (
                      <p className="text-xs text-gray-500 mt-1">Last updated on {formatDateTime(ciLastGenerated)}</p>
                    )}
                    <button
                      onClick={() => handleOpenDocumentDialog('Clinical Interview')}
                      className="mt-3 px-4 py-2 bg-violet-900 hover:bg-violet-800 text-white text-sm font-semibold rounded-md transition-colors flex items-center gap-1"
                      onMouseEnter={() => { try { prefetchCI?.(); } catch {} }}
                      onFocus={() => { try { prefetchCI?.(); } catch {} }}
                    >
                      <SearchIcon className="h-4 w-4" />
                      View
                    </button>
                  </div>
                )}

                {showSSI && (
                  <div className="flex flex-col items-center text-center">
                    <DocumentTextIcon className="h-16 w-16 text-gray-400 mx-auto mb-2" />
                    <h4 className="font-medium text-gray-800 mb-1">Semi-Structured Interview</h4>
                    {isValidTimestamp(ssiLastGenerated) && (
                      <p className="text-xs text-gray-500 mt-1">Last updated on {formatDateTime(ssiLastGenerated)}</p>
                    )}
                    <button
                      onClick={() => handleOpenDocumentDialog('Semi-Structured Interview')}
                      className="mt-3 px-4 py-2 bg-violet-900 hover:bg-violet-800 text-white text-sm font-semibold rounded-md transition-colors flex items-center gap-1"
                      onMouseEnter={() => { try { prefetchSSI?.(); } catch {} }}
                      onFocus={() => { try { prefetchSSI?.(); } catch {} }}
                    >
                      <SearchIcon className="h-4 w-4" />
                      View
                    </button>
                  </div>
                )}

                {showQbCheck && (
                  <div className="flex flex-col items-center text-center">
                    <DocumentTextIcon className="h-16 w-16 text-gray-400 mx-auto mb-2" />
                    <h4 className="font-medium text-gray-800 mb-1">QbCheck Results</h4>
                    {isValidTimestamp(qbCheckLastGenerated) && (
                      <p className="text-xs text-gray-500 mt-1">Last updated on {formatDateTime(qbCheckLastGenerated)}</p>
                    )}
                    <button
                      onClick={() => handleOpenDocumentDialog('QbCheck')}
                      className="mt-3 px-4 py-2 bg-violet-900 hover:bg-violet-800 text-white text-sm font-semibold rounded-md transition-colors flex items-center gap-1"
                      onMouseEnter={() => { try { prefetchQbCheck?.(); } catch {} }}
                      onFocus={() => { try { prefetchQbCheck?.(); } catch {} }}
                    >
                      <SearchIcon className="h-4 w-4" />
                      View
                    </button>
                  </div>
                )}

                {showQbTest && (
                  <div className="flex flex-col items-center text-center">
                    <DocumentTextIcon className="h-16 w-16 text-gray-400 mx-auto mb-2" />
                    <h4 className="font-medium text-gray-800 mb-1">QbTest Results</h4>
                    {isValidTimestamp(qbTestLastGenerated) && (
                      <p className="text-xs text-gray-500 mt-1">Last updated on {formatDateTime(qbTestLastGenerated)}</p>
                    )}
                    <button
                      onClick={() => handleOpenDocumentDialog('QbTest')}
                      className="mt-3 px-4 py-2 bg-violet-900 hover:bg-violet-800 text-white text-sm font-semibold rounded-md transition-colors flex items-center gap-1"
                      onMouseEnter={() => { try { prefetchQbTest?.(); } catch {} }}
                      onFocus={() => { try { prefetchQbTest?.(); } catch {} }}
                    >
                      <SearchIcon className="h-4 w-4" />
                      View
                    </button>
                  </div>
                )}

              </div>
            </div>
          )}

          {/* ----------------------------------------------------------------
              Panel 2: Rating Scales
              Informant data is fetched above so PatientScalesPanel can display
              informant scales without getting stuck in a permanent loading state.
          ---------------------------------------------------------------- */}
          <div className="bg-gray-50 rounded-2xl border border-gray-200 p-6 mb-6">
            <h3 className="text-lg font-semibold mb-4 text-left">Rating Scales</h3>
            <PatientScalesPanel
              patientData={patientDataModel}
              informantData={informantData}
              displayMode="full"
            />
          </div>

          {/* ----------------------------------------------------------------
              TOC: Condition(s) Referred For + Emerging Differentials
              SLaM: skipped — SSI clinician does not record differentials here;
              the Lead records those on CI Review & Synthesis.
          ---------------------------------------------------------------- */}
          {!wf.hasCIReview && (
            <>
              <div className="bg-gray-50 rounded-2xl border border-gray-200 text-left p-6 shadow-md mb-6">
                <h3 className="font-bold text-lg mb-3">Condition(s) Referred For</h3>
                <div className="rounded-lg border border-gray-200 bg-gray-100 px-4 py-3">
                  <p className="text-base text-gray-700 flex items-start gap-2">
                    <span className="text-gray-400 mt-0.5 select-none">•</span>
                    <span className="font-semibold">{ADHD_CONDITION_LABEL}</span>
                  </p>
                </div>
              </div>

              <div className="mb-6">
                <ConditionSelectionPanelTOC
                  title="Emerging Differentials"
                  variant={wf.skipDifferentialsDecisions ? 'non-report' : 'default'}
                  titleMeta={
                    isValidTimestamp(ssiLastUpdated) ? (
                      <span className="text-xs text-gray-400 italic">
                        Last updated {formatDateTime(ssiLastUpdated)}
                      </span>
                    ) : null
                  }
                  subtitle={
                    isValidTimestamp(ssiLastUpdated)
                      ? undefined
                      : 'Additional conditions under consideration from the SSI review.'
                  }
                  selectedOptions={differentials}
                  isConfirmed={true}
                  canEdit={false}
                />
              </div>
            </>
          )}

          {/* ----------------------------------------------------------------
              SLaM: SSI symptom / domain decisions (read-only), then the SSI
              clinician's emerging formulation and confidential notes so the
              Lead can see their colleague's notes at the start of DD.
          ---------------------------------------------------------------- */}
          {wf.hasCIReview && ssiDecisionTables && (
            <div className="bg-gray-50 rounded-2xl border border-gray-200 p-6 mb-6 text-left">
              <h3 className="text-lg font-semibold mb-1">
                Semi-Structured Interview decisions
              </h3>
              <p className="text-sm text-gray-600 mb-4">
                Symptom and day-to-day impact decisions recorded by{' '}
                <strong>{ssiDisplayName}</strong> during the Semi-Structured Interview.
              </p>
              <SSIDecisionSummaryTables
                compact
                attentionDeficitData={ssiDecisionTables.attentionDeficitData}
                hyperactivityImpulsivityData={ssiDecisionTables.hyperactivityImpulsivityData}
                attentionTotals={ssiDecisionTables.attentionTotals}
                hyperactivityTotals={ssiDecisionTables.hyperactivityTotals}
                domainImpairmentData={ssiDecisionTables.domainImpairmentData}
              />
            </div>
          )}

          {/* ----------------------------------------------------------------
              Emerging Formulation + Confidential Notes (read-only)
              SLaM: SSI + Lead side by side for comparison before deciding.
              TOC: single assessing-clinician panels (legacy paths).
          ---------------------------------------------------------------- */}
          <DualClinicianEmergingNotes
            showDual={wf.hasCIReview}
            ssiClinician={ssiColleagueNotes}
            leadClinician={leadColleagueNotes}
            ssiDisplayName={ssiDisplayName}
            leadDisplayName={leadDisplayName}
            ssiLastUpdated={ssiLastUpdated}
            leadLastUpdated={leadLastUpdated}
            showConfidential
            alwaysShowConfidential={wf.hasCIReview}
            singleFormulation={formulationNotes}
            singleConfidential={confidentialNotes}
            singleLastUpdated={ssiLastUpdated}
            singleVariant="amber"
          />

          {/* Bottom navigation — mirrors top bar for long-page convenience */}
          <div className="flex justify-between items-center mt-2">
            <div className="w-1/3 flex justify-start">
              <button
                onClick={() => navigate(`/patient/${patientId}`)}
                className="flex items-center py-2 px-4 rounded-md shadow-md text-gray-900 border border-gray-300 transition-all duration-300 hover:bg-gray-50"
              >
                <ChevronLeftIcon className="w-5 h-5 mr-2" aria-hidden="true" />
                <span className="font-bold">Back to Patient Overview</span>
              </button>
            </div>

            <div className="w-1/3" />

            <div className="w-1/3 flex justify-end">
              <button
                onClick={() => navigate(`/patient/${patientId}/proforma/diagnostic-decision/adhd`)}
                className="flex items-center py-2 px-4 rounded-md shadow-md bg-violet-900 hover:bg-violet-800 text-white font-bold transition-all duration-300"
              >
                <span>Next: Decision for ADHD</span>
                <ChevronRightIcon className="w-5 h-5 ml-2" aria-hidden="true" />
              </button>
            </div>
          </div>

        </main>
      </div>

      {/* Document preview iframe dialog — requires clinicianId and lastGenerated
          to construct the signed URL, mirroring the props passed in 1_DD.js. */}
      <DocumentEmbedDialog
        isOpen={isDocumentDialogOpen}
        onClose={handleCloseDocumentDialog}
        patientId={patientId}
        reportStage={selectedReport}
        clinicianId={auth.currentUser?.uid}
        lastGenerated={
          selectedReport === 'Clinical Interview'      ? ciLastGenerated
          : selectedReport === 'Semi-Structured Interview' ? ssiLastGenerated
          : selectedReport === 'QbCheck'               ? qbCheckLastGenerated
          : selectedReport === 'QbTest'                ? qbTestLastGenerated
          : null
        }
      />
    </Layout>
  );
}
